import cn from "classnames"

import { Alert } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { ExclamationCircleOutlined, InfoCircleOutlined } from "@/icons"
import { MultiVulnerabilityTooltip, RiskTooltip } from "@/molecules/RiskTooltip"

import styles from "./VulnerabilityAlert.module.scss"

import type { SearchedVulnerability } from "@/api/searchedVulnerability.types"

export const VulnerabilityAlert = (props: {
  searchedVulnerabilities?: SearchedVulnerability[]
}) => {
  const { searchedVulnerabilities } = props
  const { t } = useTranslation("translation", {
    keyPrefix: "filter.placeholder"
  })

  // Driven by the response metadata (carried on any vuln-id/riskId search), not by the search itself.
  // The array holds only ids Kodem knows, so an empty (but present) list means "none indexed".
  if (!searchedVulnerabilities) return null

  const isNoneMatching = searchedVulnerabilities.length === 0
  const type = isNoneMatching ? "warning" : "info"

  // Dates show only for a single CVE; several ids render like the HighProfile widget (gray title, no dates).
  const isSingle = searchedVulnerabilities.length === 1

  return (
    <Alert
      id="vulnerability-alert-smart-search"
      className={cn(styles.container, {
        [styles.containerInfoTheme]: type === "info",
        [styles.containerWarningTheme]: type === "warning"
      })}
      type={type}
      description={
        isNoneMatching ? (
          <div className={styles.wrapper}>
            <ExclamationCircleOutlined className={styles.icon} />
            {t("noneVulnerabilityMatching")}
          </div>
        ) : (
          <RiskTooltip
            title={
              <MultiVulnerabilityTooltip
                riskIds={searchedVulnerabilities}
                noPublishedOn={!isSingle}
              />
            }
          >
            <div className={styles.wrapper}>
              <InfoCircleOutlined className={styles.icon} />
              {t("vulnerabilityMatching", {
                count: searchedVulnerabilities.length
              })}
            </div>
          </RiskTooltip>
        )
      }
    />
  )
}
